import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { submitNetlifyForm } from "@/lib/netlifyForm";
import { useHumanCheck, HumanCheckField } from "@/components/HumanCheck";
import { useSEO } from "@/lib/seo";

const field =
  "w-full bg-white border border-line rounded-[10px] px-4 py-3 text-base focus:outline-none focus:border-orange transition-colors duration-200";
const labelCls = "block text-sm font-bold uppercase tracking-wide text-ink mb-2";

const OBJETS = ["Question générale", "Remarque", "Rectification", "Proposition", "Autre"];

export default function Contact() {
  useSEO({
    title: "Contact",
    description: "Écrire à Contre-épreuve : question, remarque ou toute autre demande.",
  });
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    objet: OBJETS[0],
    message: "",
    "bot-field": "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const check = useHumanCheck();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError("");
    if (!check.isValid) {
      setError("Réponse incorrecte à la question de vérification.");
      check.regenerate();
      return;
    }
    setLoading(true);
    try {
      await submitNetlifyForm("contact", form);
      navigate("/merci-contact");
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        navigate("/merci-contact");
        return;
      }
      setError("L'envoi a échoué. Vérifiez votre connexion et réessayez.");
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-5 md:px-8 py-14 md:py-20">
      <header>
        <p className="text-orange font-bold text-sm uppercase tracking-wide mb-4">Contact</p>
        <h1 className="font-display font-black text-5xl md:text-6xl uppercase leading-[0.92] text-ink">
          Écrire à Contre-épreuve
        </h1>
        <div className="mt-6 prose-editorial text-lg">
          <p>
            Pour proposer un article, transmettre un document ou signaler une information, utilise de
            préférence la page <Link to="/contribuer">Contribuer</Link>.
          </p>
          <p>Pour toute autre demande, tu peux contacter le projet à l'aide du formulaire ci-dessous.</p>
        </div>
      </header>

      <form
        id="contact-form"
        name="contact"
        method="POST"
        data-netlify="true"
        netlify-honeypot="bot-field"
        onSubmit={submit}
        className="mt-10 space-y-6 bg-white border border-line rounded-[18px] p-6 md:p-8"
        style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}
      >
        <input type="hidden" name="form-name" value="contact" />
        <p className="hidden" aria-hidden="true">
          <label>
            Ne pas remplir : <input name="bot-field" tabIndex={-1} autoComplete="off" onChange={set("bot-field")} value={form["bot-field"]} />
          </label>
        </p>
        <div>
          <label htmlFor="contact-name" className={labelCls}>Nom ou pseudonyme</label>
          <input id="contact-name" name="name" required value={form.name} onChange={set("name")} data-testid="contact-name" className={field} />
        </div>
        <div>
          <label htmlFor="contact-email" className={labelCls}>Adresse électronique</label>
          <input id="contact-email" name="email" type="email" required value={form.email} onChange={set("email")} data-testid="contact-email" className={field} />
        </div>
        <div>
          <label htmlFor="contact-objet" className={labelCls}>Objet</label>
          <select id="contact-objet" name="objet" value={form.objet} onChange={set("objet")} data-testid="contact-objet" className={field}>
            {OBJETS.map((o) => (
              <option key={o}>{o}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="contact-message" className={labelCls}>Message</label>
          <textarea id="contact-message" name="message" required rows={7} value={form.message} onChange={set("message")} data-testid="contact-message" className={field} />
        </div>
        <HumanCheckField check={check} testid="contact-human-check" />
        {error && (
          <p role="alert" data-testid="contact-error" className="text-sm font-medium text-[#B42318]">{error}</p>
        )}
        <button
          type="submit"
          disabled={loading}
          data-testid="contact-submit"
          className="bg-orange text-ink font-bold px-7 py-3.5 rounded-[10px] uppercase tracking-wide hover:bg-ink hover:text-orange transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
        >
          {loading ? "Envoi en cours…" : "Envoyer le message"} <ArrowRight size={17} />
        </button>
      </form>
    </div>
  );
}
